import React, { useEffect, useRef, useState } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import styles from '../styles.module.css';
import ToolButton from './ToolButton';
import { ToolFitToScreen } from '../tools/ToolFitToScreen';
import WhiteboardController from '../controllers/WhiteboardController';
import backToContentIcon from '../assets/icons/backToContent.svg';
import { svgToIcon } from '../utils/svg';
import StylusPointer from '../stylus/StylusPointer';
import { throttle } from 'lodash';

const SHOW_DELAY = 3000;

const BackToContentButton = () => {
  const controller = useController(WhiteboardController);
  return (
    <ToolButton
      className={styles.overlayToolbarButton}
      onClick={() => controller.actionApplyTool(ToolFitToScreen)}
      tool={ToolFitToScreen}
    >
      {svgToIcon(backToContentIcon)}
      Back to content
    </ToolButton>
  );
};

const OverlayToolbar: React.FC = () => {
  const [isViewEmpty, setIsViewEmpty] = useState(false);
  const [isToolbarVisible, setIsToolbarVisible] = useState(false);

  const controller = useController(WhiteboardController);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => {
    const updateViewEmptyState = throttle(
      () => {
        setIsViewEmpty(() => {
          // No menu visible
          const hasStylusMenu = controller.state.currentStylusMenu;
          if (hasStylusMenu) return false;

          // Using the pointer stylus
          const isPointer =
            controller.state.currentStylus instanceof StylusPointer;
          if (!isPointer) return false;

          // If entire canvas is empty, we won't show the button
          const isEmpty = controller.state.isEmpty;
          if (isEmpty) return false;

          // Current viewport is empty
          const isViewEmpty = controller.isViewportEmpty();
          if (!isViewEmpty) return false;

          return true;
        });
      },
      100,
      { leading: false, trailing: true }
    );

    // Attach to all the events
    const events = [
      'pan',
      'zoom',
      'object:added',
      'object:removed',
      'object:changed',
    ];
    for (const event of events) {
      controller.canvas.on(event, updateViewEmptyState);
    }

    updateViewEmptyState();

    return () => {
      for (const event of events) {
        controller.canvas.off(event, updateViewEmptyState);
      }
    };
  }, [controller.state.currentStylus]);

  useEffect(() => {
    if (isViewEmpty) {
      timer.current = setTimeout(() => {
        if (timer.current) {
          clearTimeout(timer.current);
        }
        setIsToolbarVisible(true);
      }, SHOW_DELAY);
      return clearTimer;
    }

    // Is not empty
    clearTimer();
    setIsToolbarVisible(false);
  }, [isViewEmpty]);

  if (!isToolbarVisible) {
    return null;
  }

  return (
    <div className={styles.overlayToolbar}>
      <BackToContentButton />
    </div>
  );
};

export default ApplicationView(OverlayToolbar);
